import PropTypes from 'prop-types';
import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  Error,
  ExpandLess,
  ExpandMore,
  Info,
  Warning,
} from '@mui/icons-material';

const ValidationWarnings = ({validation, sx}) => {
  const [expanded, setExpanded] = React.useState(true);

  // Simple hash function for generating stable keys
  const hashString = str => {
    let hash = 0;
    if (str.length === 0) return hash;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };

  const getMessageKey = message => {
    const keyData = JSON.stringify({
      title: message.title,
      message: message.message,
      severity: message.severity,
      affected_subscriptions: message.affected_subscriptions,
      affected_product_accesses: message.affected_product_accesses,
    });
    return `message-${hashString(keyData)}`;
  };

  if (!validation || (!validation.has_errors && !validation.has_warnings)) {
    return null;
  }

  const {messages = [], has_errors, has_warnings} = validation;

  const getSeverityIcon = severity => {
    switch (severity) {
      case 'error':
        return <Error color="error" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'info':
        return <Info color="info" />;
      default:
        return <Info color="info" />;
    }
  };

  const getSeverityColor = severity => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  };

  const getOverallSeverity = () => {
    if (has_errors) return 'error';
    if (has_warnings) return 'warning';
    return 'info';
  };

  const getAlertTitle = () => {
    if (has_errors && has_warnings) {
      return `Migration Plan Issues Detected (${messages.length})`;
    } else if (has_errors) {
      return `Migration Plan Errors Detected (${messages.length})`;
    } else if (has_warnings) {
      return `Migration Plan Warnings Detected (${messages.length})`;
    }
    return `Migration Plan Information (${messages.length})`;
  };

  return (
    <Paper sx={{...sx, mb: 2}}>
      <Alert
        severity={getOverallSeverity()}
        sx={{
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
      >
        <AlertTitle>{getAlertTitle()}</AlertTitle>
        {has_errors && (
          <Typography variant="body2" sx={{mb: 1}}>
            Please review and resolve the errors below before proceeding with
            migration.
          </Typography>
        )}
        {has_warnings && !has_errors && (
          <Typography variant="body2" sx={{mb: 1}}>
            Please review the warnings below. The migration can proceed, but
            these issues may cause problems.
          </Typography>
        )}

        <Collapse in={expanded}>
          <Box sx={{mt: 2}}>
            {messages.map(message => (
              <Box
                key={getMessageKey(message)}
                sx={{
                  mb: 2,
                  '&:last-child': {mb: 0},
                  p: 2,
                  border: 1,
                  borderColor: `${getSeverityColor(message.severity)}.light`,
                  borderRadius: 1,
                  bgcolor: `${getSeverityColor(message.severity)}.50`,
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'flex-start', mb: 1}}>
                  {getSeverityIcon(message.severity)}
                  <Box sx={{ml: 1, flex: 1}}>
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                      {message.title}
                    </Typography>
                    <Typography variant="body2" sx={{mt: 0.5}}>
                      {message.message}
                    </Typography>
                  </Box>
                </Box>

                {/* Affected Resources */}
                {(message.affected_subscriptions?.length > 0 ||
                  message.affected_product_accesses?.length > 0) && (
                  <Box sx={{mt: 2}}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{mb: 1}}
                    >
                      Affected Resources:
                    </Typography>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                      {message.affected_subscriptions?.map(subId => (
                        <Chip
                          key={`sub-${subId}`}
                          label={`Subscription ${subId}`}
                          size="small"
                          variant="outlined"
                          color={getSeverityColor(message.severity)}
                        />
                      ))}
                      {message.affected_product_accesses?.map(edpId => (
                        <Chip
                          key={`pa-${edpId}`}
                          label={edpId}
                          size="small"
                          variant="outlined"
                          color={getSeverityColor(message.severity)}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Resolution Suggestions */}
                {message.resolution_suggestions?.length > 0 && (
                  <Box sx={{mt: 2}}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{mb: 1}}
                    >
                      Suggested Actions:
                    </Typography>
                    <List dense sx={{pl: 2}}>
                      {message.resolution_suggestions.map(suggestion => (
                        <ListItem
                          key={`suggestion-${hashString(suggestion)}`}
                          sx={{py: 0.25, px: 0}}
                        >
                          <ListItemText
                            primary={suggestion}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: 'text.secondary',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Collapse>
      </Alert>
    </Paper>
  );
};

ValidationWarnings.propTypes = {
  validation: PropTypes.shape({
    has_errors: PropTypes.bool,
    has_warnings: PropTypes.bool,
    messages: PropTypes.arrayOf(
      PropTypes.shape({
        severity: PropTypes.string,
        title: PropTypes.string,
        message: PropTypes.string,
        affected_subscriptions: PropTypes.array,
        affected_product_accesses: PropTypes.array,
        resolution_suggestions: PropTypes.array,
      })
    ),
  }),
  sx: PropTypes.object,
};

export default ValidationWarnings;
